/**
 * Client entry. Mounts React into #root with BrowserRouter +
 * React Query provider. Per CHAP-78 (Phase 1C #44).
 *
 * Sentry initializes before React mounts so any errors during the
 * mount itself are captured. ErrorBoundary at the shell level
 * intercepts crashes that escape React's reconciler.
 */

// Sentry init MUST happen before any other client code so the SDK can
// instrument it. No-op if VITE_SENTRY_DSN is unset.
import { initSentry, SentryErrorBoundary } from './lib/sentry.js';
initSentry();

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { queryClient } from './lib/queryClient.js';
import { App } from './App.js';
import CookieBanner from './components/CookieBanner.js';
import './index.css';

const root = document.getElementById('root');
if (!root) throw new Error('#root not found in index.html');

function SentryFallback({
  error,
  resetError,
}: {
  error: unknown;
  resetError: () => void;
}) {
  return (
    <div style={{
      maxWidth: 480,
      margin: '64px auto',
      padding: 24,
      fontFamily: 'system-ui, sans-serif',
      color: '#03244d',
    }}>
      <h2 style={{ marginBottom: 12 }}>Something went wrong</h2>
      <p style={{ color: '#6b7280', marginBottom: 16 }}>
        We&apos;ve been notified and are looking into it. You can try reloading the page,
        or sign back in if the issue persists.
      </p>
      {import.meta.env.DEV && (
        <pre style={{
          background: '#f3f4f6',
          padding: 12,
          borderRadius: 6,
          fontSize: 12,
          overflow: 'auto',
        }}>{String(error)}</pre>
      )}
      <button
        onClick={() => {
          resetError();
          window.location.reload();
        }}
        style={{
          background: '#03244d',
          color: 'white',
          padding: '10px 20px',
          borderRadius: 6,
          border: 'none',
          cursor: 'pointer',
        }}
      >
        Reload
      </button>
    </div>
  );
}

createRoot(root).render(
  <StrictMode>
    <SentryErrorBoundary fallback={SentryFallback}>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <App />
          {/* Cookie consent banner - sits above everything else; only shows
              on first visit + after consent version bumps. */}
          <CookieBanner />
        </QueryClientProvider>
      </BrowserRouter>
    </SentryErrorBoundary>
  </StrictMode>,
);
